<template>
    <div class="vl-u-spacer--medium">
        <div class="vl-u-spacer--small">
            <canvas id="#gauge" ref="gauge"></canvas>
        </div>
        <div class="vl-grid vl-grid--is-stacked">
            <div class="vl-col--4-12 vl-u-spacer text-align-left">
                <span class="legend-box legend-bg-a-plus float-left"></span>
                <span><strong>A+</strong> {{ data.labels.APlus.replace(".", ",") }}%</span>
            </div>
        </div>
        <div class="vl-grid vl-grid--is-stacked">
            <div class="vl-col--4-12 vl-u-spacer text-align-left">
                <span class="legend-box legend-bg-a float-left"></span>
                <span><strong>A</strong> {{ data.labels.A.replace(".", ",") }}%</span>
            </div>
            <div class="vl-col--4-12 vl-u-spacer text-align-left lg-pl-6">
                <span class="legend-box legend-bg-b float-left"></span>
                <span><strong>B</strong> {{ data.labels.B.replace(".", ",") }}%</span>
            </div>
            <div class="vl-col--4-12 vl-u-spacer text-align-left lg-pl-7">
                <span class="legend-box legend-bg-c float-left"></span>
                <span class="vertical-align-top"><strong>C</strong> {{ data.labels.C.replace(".", ",") }}%</span>
            </div>
        </div>
        <div class="vl-grid vl-grid--is-stacked">
            <div class="vl-col--4-12 vl-u-spacer text-align-left">
                <span class="legend-box legend-bg-d float-left"></span>
                <span><strong>D</strong> {{ data.labels.D.replace(".", ",") }}%</span>
            </div>
            <div class="vl-col--4-12 vl-u-spacer text-align-left lg-pl-6">
                <span class="legend-box legend-bg-e float-left"></span>
                <span><strong>E</strong> {{ data.labels.E.replace(".", ",") }}%</span>
            </div>
            <div class="vl-col--4-12 vl-u-spacer text-align-left lg-pl-7">
                <span class="legend-box legend-bg-f float-left"></span>
                <span><strong>F</strong> {{ data.labels.F.replace(".", ",") }}%</span>
            </div>
        </div>
    </div>
</template>

<script>
    import {Gauge} from 'gaugeJS';

    export default {
        name: 'Gauge',
        props: ['data', 'currentEpcScore'],
        computed: {
            labelFMaxValue() {
                return Number(this.data.labels.F);
            },
            labelEMaxValue() {
                return Number(this.data.labels.F) + Number(this.data.labels.E);
            },
            labelDMaxValue() {
                return Number(this.data.labels.F) + Number(this.data.labels.E) + Number(this.data.labels.D);
            },
            labelCMaxValue() {
                return Number(this.data.labels.F) + Number(this.data.labels.E) + Number(this.data.labels.D) +
                    Number(this.data.labels.C);
            },
            labelBMaxValue() {
                return Number(this.data.labels.F) + Number(this.data.labels.E) + Number(this.data.labels.D) +
                    Number(this.data.labels.C) + Number(this.data.labels.B);
            },
            labelAMaxValue() {
                return Number(this.data.labels.F) + Number(this.data.labels.E) + Number(this.data.labels.D) +
                    Number(this.data.labels.C) + Number(this.data.labels.B) + Number(this.data.labels.A);
            },
            labelAPLUSMaxValue() {
                return Number(this.data.labels.F) + Number(this.data.labels.E) + Number(this.data.labels.D) +
                    Number(this.data.labels.C) + Number(this.data.labels.B) + Number(this.data.labels.A) +
                    Number(this.data.labels.APlus);
            },
        },
        mounted() {
            this.initGauge()
        },
        updated() {
            this.initGauge()
        },
        methods: {
            initGauge() {
                let opts = {
                    angle: -0.2,   // The span of the gauge arc
                    lineWidth: 0.25,  // The line thickness
                    radiusScale: 1,     // Relative radius
                    pointer: {
                        length: 0.45,  // Relative to gauge radius
                        strokeWidth: 0.03,  // The thickness
                        color: '#000000'    // Fill color
                    },
                    limitMax: false,      // If false, max value increases automatically if value > maxValue
                    limitMin: false,      // If true, the min value of the gauge will be fixed
                    staticZones: [
                        {strokeStyle: "#ED181B", min: 0, max: this.labelFMaxValue},                  // F
                        {strokeStyle: "#FE610F", min: this.labelFMaxValue, max: this.labelEMaxValue},  // E
                        {strokeStyle: "#FEB027", min: this.labelEMaxValue, max: this.labelDMaxValue},  // D
                        {strokeStyle: "#EDDD03", min: this.labelDMaxValue, max: this.labelCMaxValue},  // C
                        {strokeStyle: "#BDD70E", min: this.labelCMaxValue, max: this.labelBMaxValue},  // B
                        {strokeStyle: "#489323", min: this.labelBMaxValue, max: this.labelAMaxValue},  // A
                        {strokeStyle: "#286B1F", min: this.labelAMaxValue, max: this.labelAPLUSMaxValue}   // A+
                    ],
                    highDpiSupport: true  // High resolution support
                };
                let gauge = new Gauge(this.$refs.gauge);
                gauge.maxValue = this.labelAPLUSMaxValue;
                gauge.setMinValue(0);
                gauge.animationSpeed = 20;          // set animation speed (32 is default value)
                gauge.set(this.getCurrentPct());    // set actual value
                gauge.setOptions(opts);
            },
            getCurrentPct() {
                this.$emit('updated', [
                    Number(this.data.labels.APlus),
                    Number(this.data.labels.A),
                    Number(this.data.labels.B),
                    Number(this.data.labels.C),
                    Number(this.data.labels.D),
                    Number(this.data.labels.E),
                    Number(this.data.labels.F),
                ]);
                if (this.data.currentLabel === 'A+') {
                    const minEpcScoreAPlus = -100;
                    const currentEpcScoreSurPlus = this.currentEpcScore - minEpcScoreAPlus;
                    const difference = this.labelAPLUSMaxValue - this.labelAMaxValue;
                    return this.labelAPLUSMaxValue - currentEpcScoreSurPlus / 100 * difference;
                } else if (this.data.currentLabel === 'A') {
                    const minEpcScoreA = 0;
                    const currentEpcScoreSurPlus = this.currentEpcScore - minEpcScoreA;
                    const difference = this.labelAMaxValue - this.labelBMaxValue;
                    return this.labelAMaxValue - currentEpcScoreSurPlus / 100 * difference;
                } else if (this.data.currentLabel === 'B') {
                    const minEpcScoreB = 100;
                    const currentEpcScoreSurPlus = this.currentEpcScore - minEpcScoreB;
                    const difference = this.labelBMaxValue - this.labelCMaxValue;
                    return this.labelBMaxValue - currentEpcScoreSurPlus / 100 * difference;
                } else if (this.data.currentLabel === 'C') {
                    const minEpcScoreC = 200;
                    const currentEpcScoreSurPlus = this.currentEpcScore - minEpcScoreC;
                    const difference = this.labelCMaxValue - this.labelDMaxValue;
                    return this.labelCMaxValue - currentEpcScoreSurPlus / 100 * difference;
                } else if (this.data.currentLabel === 'D') {
                    const minEpcScoreD = 300;
                    const currentEpcScoreSurPlus = this.currentEpcScore - minEpcScoreD;
                    const difference = this.labelDMaxValue - this.labelEMaxValue;
                    return this.labelDMaxValue - currentEpcScoreSurPlus / 100 * difference;
                } else if (this.data.currentLabel === 'E') {
                    const minEpcScoreE = 400;
                    const currentEpcScoreSurPlus = this.currentEpcScore - minEpcScoreE;
                    const difference = this.labelEMaxValue - this.labelFMaxValue;
                    return this.labelEMaxValue - currentEpcScoreSurPlus / 100 * difference;
                } else if (this.data.currentLabel === 'F') {
                    const minEpcScoreF = 500;
                    const currentEpcScoreSurPlus = this.currentEpcScore - minEpcScoreF;
                    return this.labelFMaxValue - currentEpcScoreSurPlus / 500 * this.labelFMaxValue;
                }
            }
        }
    }
</script>
